import { SettingOutlined } from "@ant-design/icons";
import { App, Button, Checkbox, Dropdown, Modal, Select, Spin } from "antd";
import type { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { downloadDailyPendingReportExcel, getDailyPendingReport } from "../../api/entryApi";
import { formatInr } from "../../shared/utils/currency";
import { useTenantStore } from "../../stores/tenantStore";

type PendingLine = {
  id?: string | number;
  shortRemarkName?: string;
  longRemarks?: string;
  bankName?: string;
  bankAccountNumber?: string;
  ifsc?: string;
  amount?: number;
};
type PendingPaidOfGroup = { paidOfId?: string | number; paidOfName?: string; paidOfTotal?: number; lines?: PendingLine[] };
type PendingPaymentGroup = { paymentType?: string; paymentTypeTotal?: number; paidOfGroups?: PendingPaidOfGroup[] };
type PendingDateGroup = { date?: string; dateLabel?: string; dateTotal?: number; paymentTypeGroups?: PendingPaymentGroup[] };
type PendingFilters = { fromDate?: string; toDate?: string; paymentType?: string; paymentStatus?: string };
type PendingColumnKey = "date" | "paymentType" | "paidOf" | "shortRemark" | "longRemarks" | "bankName" | "bankAccountNumber" | "ifsc" | "amount";

const pendingColumns: { key: PendingColumnKey; label: string; width?: number; align?: "right" | "left" | "center" }[] = [
  { key: "date", label: "Date", width: 100, align: "center" },
  { key: "paymentType", label: "Payment Type", width: 110, align: "center" },
  { key: "paidOf", label: "PaidOf (Transfer To)", width: 190 },
  { key: "shortRemark", label: "Short Remarks", width: 170 },
  { key: "longRemarks", label: "Long Remarks", width: 260 },
  { key: "bankName", label: "Bank", width: 140 },
  { key: "bankAccountNumber", label: "Bank A/C Number", width: 160 },
  { key: "ifsc", label: "IFSC Code", width: 120 },
  { key: "amount", label: "Amount", width: 110, align: "right" }
];

const allPendingColumnKeys = pendingColumns.map((column) => column.key);

export function PendingReportModal({ open, onClose, filters }: { open: boolean; onClose: () => void; filters: Record<string, unknown> }) {
  const { message } = App.useApp();
  const tenant = useTenantStore();
  const [reportFilters, setReportFilters] = useState<PendingFilters>(() => getDefaultFilters(filters));
  const [exportingExcel, setExportingExcel] = useState(false);
  const [visibleColumns, setVisibleColumns] = usePersistedPendingColumns();

  useEffect(() => {
    if (open) setReportFilters(getDefaultFilters(filters));
  }, [open, filters]);

  const query = useQuery({
    queryKey: ["daily-pending-report", reportFilters],
    enabled: open,
    queryFn: () => getDailyPendingReport<PendingDateGroup>(cleanFilters(reportFilters))
  });

  const reportMeta = useMemo(() => {
    const parts = [
      tenant.orgName,
      tenant.financialYear ? `FY: ${tenant.financialYear}` : null,
      [reportFilters.fromDate, reportFilters.toDate].filter(Boolean).join(" to ")
    ].filter(Boolean);
    return parts.join("  |  ");
  }, [reportFilters.fromDate, reportFilters.toDate, tenant.financialYear, tenant.orgName]);

  const exportExcel = async () => {
    if (exportingExcel) return;
    if (!query.data?.length) {
      message.warning("No pending report data found for the selected filters.");
      return;
    }

    setExportingExcel(true);
    try {
      const blob = await downloadDailyPendingReportExcel({ ...cleanFilters(reportFilters), columns: visibleColumns.join(",") });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "PendingList.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      message.success("Pending report Excel download started.");
    } catch (error) {
      message.error(await getExportErrorMessage(error));
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(1120px, 96vw)"
      className="pending-report-modal"
    >
      <div className="pending-report-surface">
        <div className="pending-print-bar">
          <div className="pending-report-heading">Pending List Report</div>
          <div className="pending-print-actions">
            <Button size="small" loading={query.isFetching} disabled={exportingExcel} onClick={() => query.refetch()}>Refresh</Button>
            <Button size="small" className="btn-excel" loading={exportingExcel} disabled={query.isFetching} onClick={exportExcel}>Export Excel</Button>
            <Button size="small" className="btn-print" onClick={() => window.print()}>Print / Save PDF</Button>
            <Button size="small" onClick={onClose}>Close</Button>
          </div>
        </div>

        <div className="pending-filter-bar">
          <label>
            <span>From Date</span>
            <input type="date" value={reportFilters.fromDate ?? ""} onChange={(event) => setReportFilters((current) => ({ ...current, fromDate: event.target.value || undefined }))} />
          </label>
          <label>
            <span>To Date</span>
            <input type="date" value={reportFilters.toDate ?? ""} onChange={(event) => setReportFilters((current) => ({ ...current, toDate: event.target.value || undefined }))} />
          </label>
          <label>
            <span>Payment Type</span>
            <Select
              size="small"
              value={reportFilters.paymentType ?? ""}
              options={[{ value: "", label: "-- All --" }, { value: "Cash", label: "Cash" }, { value: "Bank", label: "Bank" }]}
              onChange={(value) => setReportFilters((current) => ({ ...current, paymentType: value || undefined }))}
            />
          </label>
          <label>
            <span>Status</span>
            <Select
              size="small"
              value={reportFilters.paymentStatus ?? ""}
              options={[{ value: "", label: "-- All --" }, { value: "Pending", label: "Pending" }, { value: "Paid", label: "Paid" }]}
              onChange={(value) => setReportFilters((current) => ({ ...current, paymentStatus: value || undefined }))}
            />
          </label>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "columns",
                  label: (
                    <Checkbox.Group
                      className="column-chooser pending-column-chooser"
                      value={visibleColumns}
                      options={pendingColumns.map((column) => ({ label: column.label, value: column.key }))}
                      onChange={(keys) => setVisibleColumns(keys.map(String) as PendingColumnKey[])}
                    />
                  )
                }
              ]
            }}
          >
            <Button size="small" icon={<SettingOutlined />}>Columns</Button>
          </Dropdown>
          <Button size="small" loading={query.isFetching} disabled={exportingExcel} onClick={() => query.refetch()}>Apply</Button>
        </div>

        {query.isLoading || query.isFetching ? <div className="pending-report-state"><Spin size="small" /> Loading report...</div> : null}
        {query.error ? <div className="pending-report-error">Failed to load report. Please try again.</div> : null}

        {!query.isLoading && !query.error ? (
          <div className="pending-report-content">
            <h2>Pending List</h2>
            <div className="pending-report-meta">{reportMeta}</div>
            <table className="pending-report-table">
              <thead>
                <tr>
                  {getVisibleColumnDefs(visibleColumns).map((column) => (
                    <th key={column.key} style={{ width: column.width }} className={column.align === "right" ? "text-right" : undefined}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {query.data?.length ? query.data.flatMap((dateGroup, index) => renderDateGroup(dateGroup, index, visibleColumns)) : (
                  <tr><td colSpan={visibleColumns.length} className="pending-empty">No records found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function renderDateGroup(dateGroup: PendingDateGroup, dateIndex: number, visibleColumns: PendingColumnKey[]) {
  const rows: JSX.Element[] = [];

  (dateGroup.paymentTypeGroups ?? []).forEach((paymentGroup, paymentIndex) => {
    (paymentGroup.paidOfGroups ?? []).forEach((paidGroup, paidIndex) => {
      (paidGroup.lines ?? []).forEach((line, lineIndex) => {
        rows.push(
          <tr className="row-detail" key={`line-${dateIndex}-${paymentIndex}-${paidIndex}-${line.id ?? lineIndex}`}>
            {getVisibleColumnDefs(visibleColumns).map((column) => (
              <td key={column.key} className={getColumnClass(column.key)}>
                {renderColumnValue(column.key, dateGroup, paymentGroup, paidGroup, line)}
              </td>
            ))}
          </tr>
        );
      });

      rows.push(
        <tr className="row-subtotal-paidof" key={`paid-total-${dateIndex}-${paymentIndex}-${paidIndex}`}>
          {renderSubtotalCells(visibleColumns, `${paidGroup.paidOfName} Total`, paidGroup.paidOfTotal ?? 0)}
        </tr>
      );
    });

    rows.push(
      <tr className="row-subtotal-paytype" key={`payment-total-${dateIndex}-${paymentIndex}`}>
        {renderSubtotalCells(visibleColumns, `${String(paymentGroup.paymentType ?? "").toUpperCase()} Total`, paymentGroup.paymentTypeTotal ?? 0)}
      </tr>
    );
  });

  rows.push(
    <tr className="row-total-date" key={`date-total-${dateIndex}`}>
      {renderSubtotalCells(visibleColumns, `${dateGroup.dateLabel ?? dateGroup.date} TOTAL`, dateGroup.dateTotal ?? 0, "date-total-label")}
    </tr>
  );
  return rows;
}

function getVisibleColumnDefs(keys: PendingColumnKey[]) {
  const selected = pendingColumns.filter((column) => keys.includes(column.key));
  return selected.length ? selected : pendingColumns;
}

function usePersistedPendingColumns() {
  const storageKey = "accounting-spa:columns:daily-pending-report";
  const [visibleColumns, setVisibleColumnsState] = useState<PendingColumnKey[]>(() => readPendingColumns(storageKey));

  const setVisibleColumns = (keys: PendingColumnKey[]) => {
    const next = keys.filter((key) => allPendingColumnKeys.includes(key));
    const safeKeys = next.length ? next : allPendingColumnKeys;
    setVisibleColumnsState(safeKeys);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(safeKeys));
    } catch {
      // Local storage may be unavailable in restricted browser contexts.
    }
  };

  return [visibleColumns, setVisibleColumns] as const;
}

function readPendingColumns(storageKey: string) {
  if (typeof window === "undefined") return allPendingColumnKeys;
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : null;
    if (!Array.isArray(parsed)) return allPendingColumnKeys;
    const keys = parsed.map(String).filter((key): key is PendingColumnKey => allPendingColumnKeys.includes(key as PendingColumnKey));
    return keys.length ? keys : allPendingColumnKeys;
  } catch {
    return allPendingColumnKeys;
  }
}

function getColumnClass(key: PendingColumnKey) {
  if (key === "date") return "date-cell";
  if (key === "paymentType") return "payment-cell";
  if (key === "paidOf") return "paidof-cell first";
  if (key === "amount") return "amount";
  return undefined;
}

function renderColumnValue(key: PendingColumnKey, dateGroup: PendingDateGroup, paymentGroup: PendingPaymentGroup, paidGroup: PendingPaidOfGroup, line: PendingLine) {
  if (key === "date") return dateGroup.dateLabel ?? dateGroup.date;
  if (key === "paymentType") return String(paymentGroup.paymentType ?? "").toUpperCase();
  if (key === "paidOf") return paidGroup.paidOfName ?? "";
  if (key === "shortRemark") return line.shortRemarkName ?? "";
  if (key === "longRemarks") return line.longRemarks ?? "";
  if (key === "bankName") return line.bankName ?? "";
  if (key === "bankAccountNumber") return line.bankAccountNumber ?? "";
  if (key === "ifsc") return line.ifsc ?? "";
  return formatInr(line.amount ?? 0);
}

function renderSubtotalCells(keys: PendingColumnKey[], label: string, amount: number, labelClass = "subtotal-label") {
  const visible = getVisibleColumnDefs(keys);
  const amountIndex = visible.findIndex((column) => column.key === "amount");
  const labelIndex = amountIndex > 0 ? amountIndex - 1 : 0;
  return visible.map((column, index) => {
    if (index === amountIndex) {
      return <td key={column.key} className="amount subtotal-amount">{formatInr(amount)}</td>;
    }
    return <td key={column.key} className={index === labelIndex ? labelClass : undefined}>{index === labelIndex ? label : ""}</td>;
  });
}

function getDefaultFilters(filters: Record<string, unknown>): PendingFilters {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const defaultFrom = `${now.getFullYear()}-${month}-01`;
  const defaultTo = now.toISOString().slice(0, 10);
  return {
    fromDate: typeof filters.fromDate === "string" ? filters.fromDate : defaultFrom,
    toDate: typeof filters.toDate === "string" ? filters.toDate : defaultTo,
    paymentType: typeof filters.paymentType === "string" ? filters.paymentType : undefined,
    paymentStatus: typeof filters.paymentStatus === "string" ? filters.paymentStatus : undefined
  };
}

function cleanFilters(filters: PendingFilters) {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== ""));
}

async function getExportErrorMessage(error: unknown) {
  const fallback = "Unable to export pending report. Please try again.";
  const responseData = (error as AxiosError)?.response?.data;
  if (responseData instanceof Blob) {
    const text = await responseData.text();
    return parseErrorText(text) ?? fallback;
  }
  if (typeof responseData === "string") return responseData || fallback;
  if (responseData && typeof responseData === "object") {
    const message = (responseData as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

function parseErrorText(text: string) {
  if (!text.trim()) return null;
  try {
    const parsed = JSON.parse(text) as { message?: unknown; title?: unknown };
    if (typeof parsed.message === "string" && parsed.message.trim()) return parsed.message;
    if (typeof parsed.title === "string" && parsed.title.trim()) return parsed.title;
  } catch {
    return text;
  }
  return text;
}
